import dayjs from 'dayjs';

export const SCRIPT_NAME = 'workbench';
export const SCRIPT_EVENTS = 'events';
export const SCRIPT_NEXT_STAGE = 'next-stage';
export const SCRIPT_EXIT = 'exit';
export const SCRIPT_CLAIM = 'claim';
export const DEFAULT_PROCESS_CODE = '10317';
export const QUERY_V3_USERS = 'getUsers';
export const REASSIGN_REASONS = ['Management Directive', 'Vacation/Leave', 'Conflict'];

export const getResolvedStaticState = ({ locationState, encodedState }) => {
	const safeLocationState = locationState && typeof locationState === 'object' ? locationState : null;
	const safeEncodedState = encodedState && typeof encodedState === 'object' ? encodedState : null;

	return {
		...(safeEncodedState ?? {}),
		...(safeLocationState ?? {}),
	};
};

export const toTrimmedString = (value) => (value ?? '').toString().trim();

const getResourceId = (row) => toTrimmedString(row?._id ?? row?.id);

const getStageKey = (row) => toTrimmedString(row?.currentStage?.stageKey);
const getStageName = (row) => toTrimmedString(row?.currentStage?.stageName);

const getEnteredAt = (row) => {
	const enteredAt = row?.currentStage?.enteredAt;
	return enteredAt?.localDateTime ?? enteredAt?.localDateTimeString ?? enteredAt?.dateString ?? null;
};

const getCreatedAt = (row) => {
	const createdAt = row?.createdAt;
	return createdAt?.localDateTime ?? createdAt?.localDateTimeString ?? createdAt?.dateString ?? null;
};

const getSlaDate = (value) => {
	const at = value && typeof value === 'object' ? value : null;
	if (!at) return null;
	return at?.localDateTime ?? at?.localDateTimeString ?? at?.dateString ?? null;
};

const getLeadData = (row) => {
	const payload = row?.data && typeof row.data === 'object' ? row.data : {};
	if (payload?.data && typeof payload.data === 'object') return payload.data;
	return payload;
};

const findCurrentTopLevelStage = (row) => {
	const stagePath = Array.isArray(row?.stagePath) ? row.stagePath : [];
	if (!stagePath.length) return null;

	const currentStageId = toTrimmedString(row?.currentStage?.stageId);
	const currentStageKey = toTrimmedString(row?.currentStage?.stageKey);

	const byId = stagePath.find((stage) => toTrimmedString(stage?.id) === currentStageId);
	if (byId) return byId;

	return stagePath.find((stage) => {
		const stageKey = toTrimmedString(stage?.stageKey);
		const parentId = toTrimmedString(stage?.parentId);
		return stageKey === currentStageKey && !parentId;
	});
};


const hasNextTopLevelStage = (row) => {
	const stagePath = Array.isArray(row?.stagePath) ? row.stagePath : [];
	if (!stagePath.length) return true;

	const topLevelStages = stagePath.filter((stage) => !toTrimmedString(stage?.parentId));
	if (!topLevelStages.length) return true;

	const currentStageId = toTrimmedString(row?.currentStage?.stageId);
	const currentStageKey = toTrimmedString(row?.currentStage?.stageKey);
	const currentIndex = topLevelStages.findIndex((stage) => {
		const stageId = toTrimmedString(stage?.id);
		const stageKey = toTrimmedString(stage?.stageKey);
		return (currentStageId && stageId === currentStageId) || (!currentStageId && stageKey === currentStageKey) || stageKey === currentStageKey;
	});

	if (currentIndex < 0) return true;
	return currentIndex < topLevelStages.length - 1;
};

const buildStageTasksFromPath = (row) => {
	const currentTopStage = findCurrentTopLevelStage(row);
	const subStages = Array.isArray(currentTopStage?.subStages) ? currentTopStage.subStages : [];
	return subStages.map((stage, index) => ({
		taskKey: toTrimmedString(stage?.stageKey) || `${index + 1}`,
		taskName: toTrimmedString(stage?.stageName) || `Task ${index + 1}`,
		required: true,
	}));
};

const buildRequiredAttachmentsByTaskKey = (row) => {
	const currentTopStage = findCurrentTopLevelStage(row);
	const subStages = Array.isArray(currentTopStage?.subStages) ? currentTopStage.subStages : [];
	const attachmentMap = new Map();

	subStages.forEach((stage) => {
		const taskKey = toTrimmedString(stage?.stageKey);
		if (!taskKey) return;
		const attachments = Array.isArray(stage?.requiredAttachments) ? stage.requiredAttachments : [];
		const normalized = attachments
			.map((attachment) => {
				const title = toTrimmedString(attachment?.title);
				return title ? { title } : null;
			})
			.filter(Boolean);
		attachmentMap.set(taskKey, normalized);
	});

	return attachmentMap;
};

const normalizeChecklistAttachments = (value) => {
	if (!value || typeof value !== 'object' || Array.isArray(value)) return {};

	const normalized = {};
	Object.entries(value).forEach(([taskKey, attachments]) => {
		const safeTaskKey = toTrimmedString(taskKey);
		if (!safeTaskKey) return;

		const safeAttachments = Array.isArray(attachments)
			? attachments
					.map((attachment) => {
						if (!attachment || typeof attachment !== 'object') return null;
						const title = toTrimmedString(attachment?.title);
						const file = attachment?.file && typeof attachment.file === 'object' ? attachment.file : null;
						return {
							title,
							file,
						};
					})
					.filter(Boolean)
			: [];

		normalized[safeTaskKey] = safeAttachments;
	});

	return normalized;
};

export const formatDuration = (value) => {
	if (!value) return '-';
	const enteredAt = dayjs(value);
	if (!enteredAt.isValid()) return '-';

	const minutes = dayjs().diff(enteredAt, 'minute');
	if (minutes < 1) return 'Just now';
	if (minutes < 60) return `${minutes}m ago`;

	const hours = Math.floor(minutes / 60);
	if (hours < 24) return `${hours}h ${minutes % 60}m ago`;

	const days = Math.floor(hours / 24);
	return `${days}d ${hours % 24}h ago`;
};

const getLeadTitle = (row) => {
	const data = getLeadData(row);
	return toTrimmedString(data?.companyName) || toTrimmedString(data?.contactPerson) || '(Untitled lead)';
};

const getLeadSubtitle = (row, { leadProductLabelByValue = {} } = {}) => {
	const data = getLeadData(row);
	const items = [];
	const interestedSolutionValue = toTrimmedString(data?.interestedSolution);
	const leadProductFromMap = toTrimmedString(leadProductLabelByValue?.[interestedSolutionValue]);
	const productDisplayName =
		leadProductFromMap ||
		toTrimmedString(data?.['interestedSolution-displayName']) ||
		toTrimmedString(data?.interestedSolutionDisplayName) ||
		toTrimmedString(data?.interestedSolutionName) ||
		toTrimmedString(data?.['leadProduct-displayName']) ||
		toTrimmedString(data?.leadProductDisplayName);
	if (toTrimmedString(data?.designation)) items.push(toTrimmedString(data.designation));
	if (productDisplayName) items.push(`Product: ${productDisplayName}`);
	if (toTrimmedString(data?.contactPerson)) items.push(`Contact: ${toTrimmedString(data.contactPerson)}`);
	return items.join(' | ');
};

export const getInitials = (name, fallback = '?') => {
	const safeName = toTrimmedString(name);
	if (!safeName) return fallback;

	const ignoreTokens = new Set(['mr', 'mrs', 'ms', 'miss', 'md', 'dr', 'prof']);
	const parts = safeName
		.split(' ')
		.map((part) => part.trim())
		.filter(Boolean)
		.filter((part) => !ignoreTokens.has(part.toLowerCase().replace(/\./g, '')));
	if (!parts.length) return fallback;

	return parts
		.slice(0, 2)
		.map((part) => part[0]?.toUpperCase() ?? '')
		.join('');
};

const parseSupportUserIds = (value) => {
	if (Array.isArray(value)) {
		return value.map((item) => toTrimmedString(item)).filter(Boolean);
	}

	const safeValue = toTrimmedString(value);
	if (!safeValue) return [];

	return safeValue
		.split(',')
		.map((item) => toTrimmedString(item))
		.filter(Boolean);
};

export const toUserOptions = (results) => {
	const rows = Array.isArray(results) ? results : [];
	const options = rows
		.map((row) => {
			const label = toTrimmedString(row?.displayName ?? row?.userFullName ?? row?.propertyValue ?? row?.propertyCode);
			const value = toTrimmedString(row?.userId ?? row?.propertyValue ?? row?.propertyCode);
			if (!label || !value) return null;
			return { label, value };
		})
		.filter(Boolean);

	const unique = new Map();
	options.forEach((option) => unique.set(option.value, option));
	return Array.from(unique.values());
};

const computeSlaProgress = ({ startAt, dueAt, fallbackHours = 24 }) => {
	const start = dayjs(startAt);
	const due = dayjs(dueAt);
	if (!start.isValid() || !due.isValid() || !due.isAfter(start)) {
		const elapsedHours = start.isValid() ? dayjs().diff(start, 'hour', true) : 0;
		const fallbackProgress = Math.max(0, Math.min(100, Math.round((elapsedHours / fallbackHours) * 100)));
		return {
			progress: fallbackProgress,
			isBreached: elapsedHours > fallbackHours,
		};
	}

	const totalMs = due.diff(start, 'millisecond');
	const elapsedMs = dayjs().diff(start, 'millisecond');
	const progress = Math.max(0, Math.min(100, Math.round((elapsedMs / totalMs) * 100)));
	return {
		progress,
		isBreached: dayjs().isAfter(due),
	};
};

export const getEventTimestamp = (event) =>
	toTrimmedString(event?.at?.localDateTimeStringAMPM) ||
	toTrimmedString(event?.at?.localDateTimeString) ||
	toTrimmedString(event?.at?.localDateTime) ||
	'-';

const toSafeJson = (value) => {
	try {
		return JSON.stringify(value);
	} catch (error) {
		return '';
	}
};

const normalizeReadableToken = (value) => {
	const safe = toTrimmedString(value);
	if (!safe) return '';
	return safe
		.replace(/[_-]+/g, ' ')
		.replace(/\s+/g, ' ')
		.trim()
		.toLowerCase()
		.replace(/\b\w/g, (letter) => letter.toUpperCase());
};

export const formatEventDetails = (event) => {
	const type = toTrimmedString(event?.type).toUpperCase();
	const data = event?.data && typeof event.data === 'object' ? event.data : {};

	if (type === 'STAGE_CHANGED') {
		const from = normalizeReadableToken(data?.from) || '-';
		const to = normalizeReadableToken(data?.to) || '-';
		return `Stage moved from ${from} to ${to}.`;
	}

	if (type === 'TASK_DONE' || type === 'CHECKLIST_UPDATED') {
		const taskKey = normalizeReadableToken(data?.taskKey || data?.checklistStageKey) || 'this checklist item';
		const doneValue = Number(data?.done);
		const totalValue = Number(data?.total);
		const done = Number.isFinite(doneValue) ? doneValue : '-';
		const total = Number.isFinite(totalValue) ? totalValue : '-';
		const completed = toTrimmedString(data?.completed).toLowerCase() === 'true' || data?.completed === true;
		const action = completed ? 'completed' : 'updated';
		if (done !== '-' && total !== '-') {
			return `Checklist item ${taskKey} ${action}. Progress is ${done} out of ${total}.`;
		}
		return `Checklist item ${taskKey} ${action}.`;
	}

	if (type === 'ADD_CHECKLIST_STAGE') {
		const stageName = normalizeReadableToken(data?.checklistStageKey || data?.stageKey) || 'a checklist item';
		return `${stageName} was added to the current stage checklist.`;
	}

	if (type === 'ASSIGNED') {
		const owner = toTrimmedString(data?.ownerFullName) || toTrimmedString(data?.ownerUserId);
		const added = Array.isArray(data?.supportUsersAdded) ? data.supportUsersAdded.length : 0;
		const removed = Array.isArray(data?.supportUsersRemoved) ? data.supportUsersRemoved.length : 0;
		if (owner) return `Owner assigned to ${owner}.`;
		if (added) return `${added} support user${added > 1 ? 's were' : ' was'} added.`;
		if (removed) return `${removed} support user${removed > 1 ? 's were' : ' was'} removed.`;
	}

	if (type === 'DATA_UPDATED') {
		const keys = Array.isArray(data?.keys) ? data.keys.filter(Boolean) : [];
		if (keys.length) return `Details were updated (${keys.map((key) => normalizeReadableToken(key)).join(', ')}).`;
	}

	const compactJson = toSafeJson(data);
	if (compactJson) return 'Activity was recorded successfully.';
	return '-';
};

export const buildRoomId = ({ processCode, itemId }) => `${toTrimmedString(processCode)}_${toTrimmedString(itemId)}`;

export const getChatTime = (message) =>
	toTrimmedString(message?.time?.localDateTimeStringAMPM) ||
	toTrimmedString(message?.time?.localDateTimeString) ||
	toTrimmedString(message?.time?.localDateTime) ||
	'-';

export const toWorkBenchCards = (responseData, { leadProductLabelByValue = {} } = {}) => {
	const rows = Array.isArray(responseData) ? responseData : [];
	return rows
		.map((row) => {
			const id = getResourceId(row);
			if (!id) return null;

			const enteredAt = getEnteredAt(row);
			const createdAt = getCreatedAt(row);
			const stageDueAt = getSlaDate(row?.sla?.dueAt);
			const globalDueAt = getSlaDate(row?.sla?.globalDueAt);
			const stageSla = computeSlaProgress({ startAt: enteredAt, dueAt: stageDueAt, fallbackHours: 24 });
			const totalSla = computeSlaProgress({ startAt: createdAt, dueAt: globalDueAt, fallbackHours: 48 });
			const isBreach = Boolean(row?.sla?.breached) || stageSla.isBreached;
			const stageTasks =
				Array.isArray(row?.stageTasks) && row.stageTasks.length
					? row.stageTasks
					: buildStageTasksFromPath(row);
			const completedTaskKeys = Array.isArray(row?.completedTaskKeys) ? row.completedTaskKeys.map((key) => toTrimmedString(key)) : [];
			const supportTeam = Array.isArray(row?.supportTeam) ? row.supportTeam : [];
			const supportMembersFromTeam = supportTeam
				.map((support) => {
					const userId = toTrimmedString(support?.entityCode ?? support?.userId);
					if (!userId) return null;
					const fullName = toTrimmedString(support?.entityFullName ?? support?.displayName ?? support?.userFullName) || userId;
					const initials = getInitials(fullName, userId.substring(0, 2).toUpperCase());
					return {
						userId,
						fullName,
						initials,
					};
				})
				.filter(Boolean);
			const supportUserNames = Array.isArray(row?.supportUserNames) ? row.supportUserNames : [];
			const supportMembersFromNames = supportUserNames
				.map((fullName, index) => {
					const safeName = toTrimmedString(fullName);
					const userId = toTrimmedString(row?.supportUserIds?.[index] ?? safeName);
					if (!safeName && !userId) return null;
					return {
						userId: userId || safeName,
						fullName: safeName || userId,
						initials: getInitials(safeName || userId, getInitials(userId, 'U')),
					};
				})
				.filter(Boolean);
			const supportByUserId = new Map([...supportMembersFromTeam, ...supportMembersFromNames].map((support) => [support.userId, support]));
			parseSupportUserIds(row?.supportUserIds).forEach((supportUserId) => {
				if (!supportByUserId.has(supportUserId)) {
					supportByUserId.set(supportUserId, {
						userId: supportUserId,
						fullName: supportUserId,
						initials: getInitials(supportUserId, supportUserId.substring(0, 2).toUpperCase()),
					});
				}
			});
			const supportMembers = Array.from(supportByUserId.values());
			const data = getLeadData(row);
			const requiredAttachmentsByTaskKey = buildRequiredAttachmentsByTaskKey(row);
			const checklistAttachments = normalizeChecklistAttachments(data?.checklistAttachments);
			const normalizedTasks = stageTasks.map((task, index) => ({
				taskKey: toTrimmedString(task?.taskKey) || `${index + 1}`,
				taskName: toTrimmedString(task?.taskName) || `Task ${index + 1}`,
				required: Boolean(task?.required),
				done: completedTaskKeys.includes(toTrimmedString(task?.taskKey)),
				requiredAttachments: requiredAttachmentsByTaskKey.get(toTrimmedString(task?.taskKey)) ?? [],
				uploadedAttachments: checklistAttachments[toTrimmedString(task?.taskKey)] ?? [],
			}));
			const requiredTasks = normalizedTasks.filter((task) => task.required);
			const allRequiredTasksDone = requiredTasks.length ? requiredTasks.every((task) => task.done) : true;

			return {
				id,
				title: getLeadTitle(row),
				subtitle: getLeadSubtitle(row, { leadProductLabelByValue }),
				leadCode: toTrimmedString(data?.leadCode),
				leadSource: toTrimmedString(data?.leadSource),
				phoneNumber: toTrimmedString(data?.phoneNumber),
				emailAddress: toTrimmedString(data?.emailAddress),
				initialNotes: toTrimmedString(data?.initialNotes),
				stageKey: getStageKey(row) || 'UNKNOWN',
				stageName: getStageName(row),
				waitTime: formatDuration(enteredAt),
				owner: toTrimmedString(row?.ownerFullName) || toTrimmedString(row?.ownerUserName) || toTrimmedString(row?.ownerUserId) || '-',
				ownerUserId: toTrimmedString(row?.ownerUserId),
				ownerImage:
					toTrimmedString(row?.ownerImage) ||
					toTrimmedString(row?.ownerUserImage) ||
					toTrimmedString(row?.ownerAvatar) ||
					toTrimmedString(row?.ownerPhotoUrl) ||
					'',
				ownerInitials: getInitials(
					toTrimmedString(row?.ownerFullName) || toTrimmedString(row?.ownerUserName),
					getInitials(toTrimmedString(row?.ownerUserId), 'U')
				),
				supportMembers,
				supportCount: supportMembers.length,
				priority: toTrimmedString(data?.priorityLevel) || '-',
				stageProgress: stageSla.progress,
				totalProgress: totalSla.progress,
				isBreach,
				stageTasks: normalizedTasks,
				checklistAttachments,
				allRequiredTasksDone,
				hasNextStage: hasNextTopLevelStage(row),
			};
		})
		.filter(Boolean);
};

export const toWorkBenchCardBuckets = (responseData, { leadProductLabelByValue = {} } = {}) => {
	if (Array.isArray(responseData)) {
		return {
			inbound: toWorkBenchCards(responseData, { leadProductLabelByValue }),
			outbound: [],
			collab: [],
		};
	}

	const safe = responseData && typeof responseData === 'object' ? responseData : {};
	return {
		inbound: toWorkBenchCards(safe?.inbound, { leadProductLabelByValue }),
		outbound: toWorkBenchCards(safe?.outbound, { leadProductLabelByValue }),
		collab: toWorkBenchCards(safe?.collab, { leadProductLabelByValue }),
	};
};
